// FramesGrid.tsx
import { FC, useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import Colors from "../../../ui/global/Colors";
import ButtonMicro from "../../../ui/components/ButtonMicro";
import LabelFrames from "./LabelFrames";
import { EditBarCheckbox } from "./EditBarItem";
import Fonts from "../../../ui/global/Fonts";

const TARGET_FRAME_SIZE = 36; 
const TARGET_FRAME_SIZE_MOBILE = 30;

const MIN_FRAME_SIZE = 30;
const MIN_FRAME_SIZE_MOBILE = 24;

const DRAG_THRESHOLD = 5;

const White = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: auto;
    background-color: ${Colors.white};
    border-radius: 2px;
    border: 1px solid ${Colors.grey1};
`;

const FramesContainer = styled(motion.ul)<{ $targetSize: number }>`
    position: relative;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(${props => props.$targetSize}px, 1fr));
    align-items: flex-start;
    justify-items: stretch;
    width: 100%;
    height: auto;
    gap: 0px 2px;
    overflow: visible; 
    list-style-type: none;
    padding-left: 0;
    padding-bottom: 0;
    margin: 0;
    user-select: none; /* Prevent text selection during drag */
    touch-action: none; /* Prevent scrolling while interacting with the grid */

    > li, > ${White} {
        width: 100%;
        height: auto;
        aspect-ratio: 1 / 1;
        cursor: pointer;
        overflow: visible;
        margin: 1px; 
    }
`;

const ImageContainer = styled(motion.div).attrs<{ $isCurrentFrame?: boolean; $isHole?: boolean; $isHidden?: boolean; $isOutsideRange?: boolean }>(props => ({
    style: {
        opacity: props.$isHole ? 0 : 1,
        borderColor: props.$isCurrentFrame ? Colors.grey6 : Colors.grey1,
    },
}))<{ $isAnimation?: boolean; $isCurrentFrame?: boolean; $isHole?: boolean; $isDraggingActive?: boolean; $isOutsideRange?: boolean; $isHidden?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background-color: ${Colors.white};
    border-radius: 2px;
    border-style: solid;
    border-width: 1px;
    overflow: hidden;
    margin: 0;

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        background-image: url("/assets/transparent-background.svg");
        background-size: cover;
        background-repeat: no-repeat;
        opacity: 1;
        z-index: 0;
    }

    ${props => !props.$isAnimation && !props.$isDraggingActive && `
        &:hover {
            border: 1px solid ${Colors.grey4};
        }
    `}
`;

const AnimationImage = styled.img.attrs<{ $isDimmed?: boolean }>(props => ({
    style: {
        opacity: props.$isDimmed ? 0.2 : 1,
    },
}))<{ $isAnimation?: boolean; $isDimmed?: boolean }>`
    width: 100%;
    height: 100%;
    object-fit: contain;
    user-select: none;
    pointer-events: ${props => props.$isAnimation ? 'auto' : 'none'};
    z-index: 1;
`;

const DeleteButton = styled.div`
    position: absolute;
    top: 2px;
    right: 2px;
    z-index: 10;
`;

const FrameNumber = styled.div`
    position: absolute;
    bottom: 0px;
    right: 0px;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 8px;
    color: ${Colors.grey8};
    line-height: 1;
    pointer-events: none;
    z-index: 5;
`;

const DragOverlayContainer = styled.div.attrs<{ $x: number; $y: number; $width: number; $height: number }>(props => ({
    style: {
        transform: `translate(${props.$x}px, ${props.$y}px)`,
        width: `${props.$width}px`,
        height: `${props.$height}px`,
    }
}))<{ $x: number; $y: number; $width: number; $height: number }>`
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
    pointer-events: none; /* Let events pass through to detect underlying elements */
    background-color: ${Colors.white};
    border-radius: 2px;
    border: 1px solid ${Colors.grey6};
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const CheckboxContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
`;

export interface FramesProps {
    orderedImages: string[];
    currentFrameIndex: number;
    numOfFrames: number;
    startFrame: number;
    endFrame: number;
    handleReorder: (newOrder: string[]) => void;
    onFrameClick: (index: number) => void;
    setIsPlaying: (isPlaying: boolean) => void;
    isMobile: boolean;
    hiddenFrames?: number[];
}

const FramesGrid: FC<FramesProps> = ({ 
    orderedImages, 
    currentFrameIndex, 
    numOfFrames, 
    startFrame, 
    endFrame, 
    handleReorder, 
    onFrameClick, 
    setIsPlaying,
    isMobile,
    hiddenFrames = [],
}) => {
    const [hoveredFrameIndex, setHoveredFrameIndex] = useState<number | null>(null);
    const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
    const [dragStartIndex, setDragStartIndex] = useState<number | null>(null);
    const [dragStartPoint, setDragStartPoint] = useState<{ x: number, y: number } | null>(null);
    const [dragCurrentPoint, setDragCurrentPoint] = useState<{ x: number, y: number } | null>(null);
    const [dragElementRect, setDragElementRect] = useState<{ width: number, height: number, offsetX: number, offsetY: number } | null>(null);

    const targetFrameSize = isMobile ? TARGET_FRAME_SIZE_MOBILE : TARGET_FRAME_SIZE;

    const getIndexFromElement = (element: Element | null): number | null => {
        if (!element) return null;
        const li = element.closest('li');
        if (li && li.hasAttribute('data-index')) {
            return parseInt(li.getAttribute('data-index') || '-1', 10);
        }
        return null;
    };

    const handlePointerDown = (e: React.PointerEvent, index: number) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        
        // Initial drag state
        setDragStartIndex(index);
        setDragStartPoint({ x: e.clientX, y: e.clientY });
        setDragCurrentPoint({ x: e.clientX, y: e.clientY });
        setDragElementRect({
            width: rect.width,
            height: rect.height,
            offsetX: e.clientX - rect.left,
            offsetY: e.clientY - rect.top
        });
        
        // Capture pointer
        target.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: React.PointerEvent, index: number) => {
        if (!dragStartPoint) return;

        setDragCurrentPoint({ x: e.clientX, y: e.clientY });

        if (draggingIndex === null) {
            // Check threshold
            const dx = e.clientX - dragStartPoint.x;
            const dy = e.clientY - dragStartPoint.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist > DRAG_THRESHOLD) {
                // START DRAGGING
                setIsPlaying(false);
                setDraggingIndex(index);
            }
        } else {
            // We are dragging.
            // Check for reorder targets.
            const target = document.elementFromPoint(e.clientX, e.clientY);
            const targetIndex = getIndexFromElement(target);

            if (targetIndex !== null && targetIndex >= 0 && targetIndex !== draggingIndex) {
                 // REORDER logic
                 const newOrder = [...orderedImages];
                 // Remove from old pos
                 const [movedItem] = newOrder.splice(draggingIndex, 1);
                 // Insert at new pos
                 newOrder.splice(targetIndex, 0, movedItem);
                 
                 handleReorder(newOrder);
                 // Update draggingIndex because our item moved to a new index
                 setDraggingIndex(targetIndex);
            }
        }
    };

    const handlePointerUp = (e: React.PointerEvent) => {
         if (draggingIndex === null && dragStartIndex !== null) {
            // We didn't drag (didn't exceed threshold), so treat this as a click
            onFrameClick(dragStartIndex);
        }

        // Reset
        setDragStartPoint(null);
        setDragCurrentPoint(null);
        setDragElementRect(null);
        setDraggingIndex(null);
        setDragStartIndex(null);
        
        const target = e.currentTarget as HTMLElement;
        target.releasePointerCapture(e.pointerId);
    };

    // calculate overlay position
    const overlayPos = useMemo(() => {
        if (!dragCurrentPoint || !dragElementRect) return null;
        return {
            x: dragCurrentPoint.x - dragElementRect.offsetX,
            y: dragCurrentPoint.y - dragElementRect.offsetY,
            width: dragElementRect.width,
            height: dragElementRect.height
        };
    }, [dragCurrentPoint, dragElementRect]);

    return (
        <>
            <FramesContainer id="frames-grid-container" $targetSize={targetFrameSize}>
                {orderedImages.map((image, i) => {
                    const isOutsideRange = i < startFrame || i > endFrame; // Check if frame is outside the range
                    const isHidden = hiddenFrames.includes(i);
                    return (
                        <motion.li 
                            key={`${image}-${i}`} 
                            data-frame-key={image}
                            data-index={i}
                            layout
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            onPointerDown={(e) => handlePointerDown(e, i)}
                            onPointerMove={(e) => handlePointerMove(e, i)}
                            onPointerUp={handlePointerUp}
                            onPointerCancel={handlePointerUp} // Handle cancellation safely
                        >
                            <ImageContainer 
                                $isCurrentFrame={i === currentFrameIndex}
                                $isHole={draggingIndex === i} 
                                $isDraggingActive={draggingIndex !== null}
                                $isOutsideRange={isOutsideRange} // Pass new prop to styled component
                                $isHidden={isHidden}
                                // Click handled in handlePointerUp now to avoid PointerCapture conflicts
                                onPointerDown={(e) => {
                                    // Stop propagation for DeleteButton
                                    if (e.currentTarget.contains(e.target as Node) && (e.target as HTMLElement).closest(DeleteButton.toString())) {
                                        e.stopPropagation();
                                    }
                                }}
                                whileHover={{ 
                                    scale: draggingIndex === null ? 1.02 : 1,
                                    zIndex: draggingIndex === null ? 90 : 0, 
                                }}
                                onMouseEnter={() => setHoveredFrameIndex(i)}
                                onMouseLeave={() => setHoveredFrameIndex(null)}
                            >
                                {/* <FrameNumber>{i + 1}</FrameNumber> */}
                                <AnimationImage 
                                    src={image} 
                                    alt={`Frame ${i+1}`} 
                                    $isDimmed={isOutsideRange || isHidden}
                                />
                            </ImageContainer>
                        </motion.li>
                    )
                })}
                {/* Empty placeholders */}
                {Array.from({ length: Math.max(0, Math.floor(numOfFrames) - orderedImages.length) }).map((_, i) => (
                    <White key={`empty-${i}`} />
                ))}

                {/* <LabelFrames currentFrameNumber={currentFrameIndex+1} maxFrames={numOfFrames} /> */}
                {/* {numOfFrames > 0 && (
                    <CheckboxContainer>
                        <EditBarCheckbox 
                            text="ALL" 
                            checked={false}
                            onClick={()=>{}}
                            // checked={isSelectAllChecked}
                            // onClick={handleSelectAllClick}
                            orientation={'vertical'}
                        />
                    </CheckboxContainer>
                )} */}
            </FramesContainer>

            {/* Portal Overlay */}
            {draggingIndex !== null && overlayPos && orderedImages[draggingIndex] && createPortal(
                <DragOverlayContainer 
                    $x={overlayPos.x} 
                    $y={overlayPos.y}
                    $width={overlayPos.width}
                    $height={overlayPos.height}
                >
                     <AnimationImage src={orderedImages[draggingIndex]} alt="Dragging" />
                </DragOverlayContainer>,
                document.body
            )}
        </>
    );
};

export default FramesGrid;